<template>
  <div class="up-table-wrapper">
    <!-- Slot to register column children. They render nothing. -->
    <slot></slot>

    <div v-if="searchable" class="up-table-toolbar">
      <div class="up-table-search">
        <span class="up-table-search-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
        </span>
        <input v-model="search" type="text" :placeholder="searchPlaceholder" />
      </div>
    </div>

    <div class="up-table-scroll">
      <table class="up-table" :class="{ 'is-hoverable': hoverable, 'is-striped': striped }">
        <thead>
          <tr>
            <th v-if="checkable" class="up-checkbox-cell">
              <label class="up-checkbox">
                <input
                  type="checkbox"
                  :checked="allOnPageChecked"
                  :indeterminate.prop="someOnPageChecked && !allOnPageChecked"
                  @change="toggleAllOnPage($event.target.checked)"
                />
                <span class="up-checkbox-mark"></span>
              </label>
            </th>
            <th
              v-for="(col, i) in columns"
              :key="`h-${i}`"
              :style="col.width ? `width: ${col.width};` : ''"
              :class="{ 'is-sortable': col.sortable }"
              @click="col.sortable ? toggleSort(col) : null"
            >
              <span class="up-th-content">
                <span>{{ col.label }}</span>
                <span v-if="col.sortable" class="up-sort-indicator" :class="sortIndicatorClass(col)">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                </span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="totalColumnCount" class="up-table-state">
              <div class="up-spinner"></div>
            </td>
          </tr>
          <tr v-else-if="!pagedData.length">
            <td :colspan="totalColumnCount" class="up-table-state">
              <slot name="empty">
                <div class="up-empty">
                  <div class="up-empty-emoji">📭</div>
                  <div class="up-empty-text">No data to display</div>
                </div>
              </slot>
            </td>
          </tr>
          <tr v-for="(row, rIdx) in pagedData" :key="rowKey(row, rIdx)" :class="{ 'is-checked': checkable && isChecked(row) }">
            <td v-if="checkable" class="up-checkbox-cell" data-label="">
              <label class="up-checkbox" @click.stop>
                <input
                  type="checkbox"
                  :checked="isChecked(row)"
                  @change="toggleRow(row, $event.target.checked)"
                />
                <span class="up-checkbox-mark"></span>
              </label>
            </td>
            <td
              v-for="(col, cIdx) in columns"
              :key="`c-${rIdx}-${cIdx}`"
              :data-label="col.label"
            >
              <up-cell :col="col" :row="row" :index="rIdx" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="paginated && totalPages > 1" class="up-table-pagination">
      <div class="up-pagination-info">
        Showing
        <strong>{{ rangeStart }}</strong>
        –
        <strong>{{ rangeEnd }}</strong>
        of
        <strong>{{ sortedData.length }}</strong>
      </div>
      <div class="up-pagination-controls">
        <button
          class="up-page-btn"
          :disabled="currentPage === 1"
          @click="goTo(1)"
          aria-label="First page"
        >«</button>
        <button
          class="up-page-btn"
          :disabled="currentPage === 1"
          @click="goTo(currentPage - 1)"
          aria-label="Previous page"
        >‹</button>
        <button
          v-for="p in pageNumbers"
          :key="p"
          class="up-page-btn"
          :class="{ 'is-active': p === currentPage }"
          @click="goTo(p)"
        >{{ p }}</button>
        <button
          class="up-page-btn"
          :disabled="currentPage === totalPages"
          @click="goTo(currentPage + 1)"
          aria-label="Next page"
        >›</button>
        <button
          class="up-page-btn"
          :disabled="currentPage === totalPages"
          @click="goTo(totalPages)"
          aria-label="Last page"
        >»</button>
      </div>
    </div>
  </div>
</template>

<script>
const UpCell = {
  name: 'UpCell',
  functional: true,
  props: {
    col: { type: Object, required: true },
    row: { type: Object, required: true },
    index: { type: Number, default: 0 }
  },
  render(h, { props }) {
    const { col, row, index } = props
    const slot = col.$scopedSlots && col.$scopedSlots.default
    if (slot) return slot({ row, index })
    if (col.field) {
      const val = row[col.field]
      return h('span', val == null ? '' : String(val))
    }
    return h('span', '')
  }
}

export default {
  name: 'UpTable',
  components: { UpCell },
  provide() {
    return {
      registerColumn: this.registerColumn,
      unregisterColumn: this.unregisterColumn
    }
  },
  props: {
    data: { type: Array, default: () => [] },
    paginated: { type: Boolean, default: false },
    perPage: { type: [Number, String], default: 20 },
    rowKeyField: { type: String, default: '_id' },
    striped: { type: Boolean, default: false },
    hoverable: { type: Boolean, default: true },
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: 'Search...' },
    loading: { type: Boolean, default: false },
    checkable: { type: Boolean, default: false },
    checkedRows: { type: Array, default: () => [] }
  },
  data() {
    return {
      columns: [],
      currentPage: 1,
      sortField: null,
      sortDir: 'asc',
      search: ''
    }
  },
  computed: {
    perPageNum() {
      const n = parseInt(this.perPage, 10)
      return isNaN(n) || n < 1 ? 20 : n
    },
    filteredData() {
      if (!this.searchable || !this.search.trim()) return this.data
      const q = this.search.trim().toLowerCase()
      return (this.data || []).filter(row => {
        return this.columns.some(col => {
          if (!col.field) return false
          const val = row[col.field]
          return val != null && String(val).toLowerCase().includes(q)
        })
      })
    },
    sortedData() {
      const list = this.filteredData || []
      if (!this.sortField) return list
      const dir = this.sortDir === 'asc' ? 1 : -1
      const field = this.sortField
      return [...list].sort((a, b) => {
        const av = a[field]
        const bv = b[field]
        if (av == null && bv == null) return 0
        if (av == null) return -1 * dir
        if (bv == null) return 1 * dir
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
        return String(av).localeCompare(String(bv)) * dir
      })
    },
    totalPages() {
      if (!this.paginated) return 1
      return Math.max(1, Math.ceil(this.sortedData.length / this.perPageNum))
    },
    pagedData() {
      if (!this.paginated) return this.sortedData
      const start = (this.currentPage - 1) * this.perPageNum
      return this.sortedData.slice(start, start + this.perPageNum)
    },
    rangeStart() {
      if (!this.sortedData.length) return 0
      return (this.currentPage - 1) * this.perPageNum + 1
    },
    rangeEnd() {
      return Math.min(this.currentPage * this.perPageNum, this.sortedData.length)
    },
    totalColumnCount() {
      return (this.columns.length || 1) + (this.checkable ? 1 : 0)
    },
    allOnPageChecked() {
      if (!this.pagedData.length) return false
      return this.pagedData.every(r => this.isChecked(r))
    },
    someOnPageChecked() {
      return this.pagedData.some(r => this.isChecked(r))
    },
    pageNumbers() {
      const total = this.totalPages
      const cur = this.currentPage
      const win = 1
      const pages = new Set([1, total, cur, cur - win, cur + win])
      const arr = [...pages].filter(p => p >= 1 && p <= total).sort((a, b) => a - b)
      const out = []
      for (let i = 0; i < arr.length; i++) {
        if (i > 0 && arr[i] - arr[i - 1] > 1) out.push('…')
        out.push(arr[i])
      }
      return out.filter(p => typeof p === 'number')
    }
  },
  watch: {
    data() {
      if (this.currentPage > this.totalPages) this.currentPage = this.totalPages
    },
    search() {
      this.currentPage = 1
    }
  },
  methods: {
    registerColumn(col) {
      if (!this.columns.includes(col)) this.columns.push(col)
    },
    unregisterColumn(col) {
      this.columns = this.columns.filter(c => c !== col)
    },
    rowKey(row, idx) {
      return (this.rowKeyField && row && row[this.rowKeyField]) || idx
    },
    toggleSort(col) {
      if (!col.field) return
      if (this.sortField === col.field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = col.field
        this.sortDir = 'asc'
      }
    },
    sortIndicatorClass(col) {
      if (this.sortField !== col.field) return 'is-idle'
      return this.sortDir === 'asc' ? 'is-asc' : 'is-desc'
    },
    goTo(p) {
      if (p < 1 || p > this.totalPages) return
      this.currentPage = p
    },
    isChecked(row) {
      if (!this.checkable) return false
      const key = this.rowKey(row, -1)
      return this.checkedRows.some(r => this.rowKey(r, -2) === key)
    },
    toggleRow(row, checked) {
      const key = this.rowKey(row, -1)
      let next
      if (checked) {
        if (this.isChecked(row)) return
        next = [...this.checkedRows, row]
      } else {
        next = this.checkedRows.filter(r => this.rowKey(r, -2) !== key)
      }
      this.$emit('update:checkedRows', next)
    },
    toggleAllOnPage(checked) {
      let next = [...this.checkedRows]
      if (checked) {
        for (const r of this.pagedData) {
          if (!this.isChecked(r)) next.push(r)
        }
      } else {
        const pageKeys = new Set(this.pagedData.map(r => this.rowKey(r, -1)))
        next = next.filter(r => !pageKeys.has(this.rowKey(r, -2)))
      }
      this.$emit('update:checkedRows', next)
    }
  }
}
</script>

<style scoped>
.up-table-wrapper {
  width: 100%;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
  overflow: hidden;
}

.up-table-toolbar {
  padding: 0.85rem 1rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, #fafbfd, #ffffff);
}

.up-table-search {
  position: relative;
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 10px;
  padding: 0.45rem 0.75rem;
  max-width: 320px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.up-table-search:focus-within {
  border-color: #8919e7;
  box-shadow: 0 0 0 3px rgba(137, 25, 231, 0.12);
}
.up-table-search-icon {
  display: flex;
  color: #94a3b8;
  margin-right: 0.5rem;
}
.up-table-search input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  color: #1f2937;
}

.up-table-scroll {
  width: 100%;
  overflow-x: auto;
}

.up-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;
}

.up-table thead th {
  text-align: left;
  font-weight: 600;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  padding: 0.85rem 1rem;
  background: #f8fafc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  white-space: nowrap;
  user-select: none;
}

.up-table thead th.is-sortable {
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
}
.up-table thead th.is-sortable:hover {
  color: #1f2937;
  background: #f1f5f9;
}

.up-th-content {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.up-sort-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  color: #cbd5e1;
  transition: transform 0.2s ease, color 0.15s ease;
}
.up-sort-indicator.is-asc {
  color: #8919e7;
  transform: rotate(180deg);
}
.up-sort-indicator.is-desc {
  color: #8919e7;
}

.up-table tbody td {
  padding: 0.85rem 1rem;
  color: #1f2937;
  border-bottom: 1px solid rgba(15, 23, 42, 0.05);
  vertical-align: middle;
}

.up-table.is-striped tbody tr:nth-child(even) td {
  background: #fafbfd;
}

.up-table.is-hoverable tbody tr {
  transition: background 0.15s ease;
}
.up-table.is-hoverable tbody tr:hover td {
  background: rgba(137, 25, 231, 0.04);
}

.up-table tbody tr:last-child td {
  border-bottom: none;
}

.up-checkbox-cell {
  width: 40px;
  padding-right: 0 !important;
}
.up-checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
}
.up-checkbox input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.up-checkbox-mark {
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid #cbd5e1;
  background: #ffffff;
  transition: background 0.15s ease, border-color 0.15s ease;
  position: relative;
}
.up-checkbox-mark::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 1px;
  width: 5px;
  height: 10px;
  border: solid #ffffff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg) scale(0);
  transition: transform 0.15s ease;
}
.up-checkbox input:checked + .up-checkbox-mark {
  background: #8919e7;
  border-color: #8919e7;
}
.up-checkbox input:checked + .up-checkbox-mark::after {
  transform: rotate(45deg) scale(1);
}
.up-checkbox input:indeterminate + .up-checkbox-mark {
  background: #8919e7;
  border-color: #8919e7;
}
.up-checkbox input:indeterminate + .up-checkbox-mark::after {
  content: '';
  border: none;
  background: #ffffff;
  width: 8px;
  height: 2px;
  left: 4px;
  top: 7px;
  transform: none;
  border-radius: 1px;
}
.up-table tbody tr.is-checked td {
  background: rgba(137, 25, 231, 0.06);
}

.up-table-state {
  text-align: center;
  padding: 2.5rem 1rem !important;
  color: #94a3b8;
}

.up-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}
.up-empty-emoji {
  font-size: 2rem;
}
.up-empty-text {
  font-size: 0.95rem;
  color: #64748b;
}

.up-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(137, 25, 231, 0.2);
  border-top-color: #8919e7;
  border-radius: 50%;
  margin: 0 auto;
  animation: up-spin 0.8s linear infinite;
}
@keyframes up-spin {
  to { transform: rotate(360deg); }
}

.up-table-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fafbfd;
}

.up-pagination-info {
  font-size: 0.85rem;
  color: #64748b;
}
.up-pagination-info strong {
  color: #1f2937;
  font-weight: 600;
}

.up-pagination-controls {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
.up-page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 0.55rem;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: #ffffff;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #475569;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.1s ease;
}
.up-page-btn:hover:not(:disabled):not(.is-active) {
  background: #f1f5f9;
  border-color: rgba(15, 23, 42, 0.18);
  color: #1f2937;
}
.up-page-btn:active:not(:disabled) {
  transform: translateY(1px);
}
.up-page-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.up-page-btn.is-active {
  background: #8919e7;
  border-color: #8919e7;
  color: #ffffff;
  font-weight: 600;
}

@media screen and (max-width: 640px) {
  .up-table thead {
    display: none;
  }
  .up-table tbody td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 0.65rem 1rem;
    border-bottom: 1px solid rgba(15, 23, 42, 0.05);
  }
  .up-table tbody td::before {
    content: attr(data-label);
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    flex-shrink: 0;
  }
  .up-table tbody tr {
    display: block;
    padding: 0.5rem 0;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  }
  .up-table tbody tr:last-child {
    border-bottom: none;
  }
  .up-table-pagination {
    justify-content: center;
  }
  .up-pagination-info {
    width: 100%;
    text-align: center;
  }
}
</style>
